import type { AORHierarchyNode } from './schema';

export type AorTree = {
  childrenByParent: Record<string, AORHierarchyNode[]>;
  nodesById: Record<string, AORHierarchyNode>;
};

/**
 * Turns the flat AOR payload into a parent/child index. Input order is
 * preserved within each sibling list, so the backend's `levelOrder`-then-name
 * ordering carries through. A node whose parent is missing from the payload is
 * left out of `childrenByParent` so the caller renders it at the top level
 * instead of stranding its whole subtree.
 */
export const buildAorTree = (nodes: AORHierarchyNode[]): AorTree => {
  const nodesById: Record<string, AORHierarchyNode> = {};
  nodes.forEach((node) => {
    nodesById[node.id] = node;
  });

  const childrenByParent: Record<string, AORHierarchyNode[]> = {};

  nodes.forEach((node) => {
    const parentId = node.parentItemId;
    if (!parentId || parentId === node.id || !nodesById[parentId]) return;

    if (!childrenByParent[parentId]) {
      childrenByParent[parentId] = [];
    }
    childrenByParent[parentId].push(node);
  });

  return { childrenByParent, nodesById };
};

/**
 * All ids beneath `nodeId`, excluding itself. The `seen` set makes this safe
 * against a parent cycle; upsert rejects cycles, but a bad row should not hang
 * the tab.
 */
export const collectDescendantIds = (
  childrenByParent: Record<string, AORHierarchyNode[]>,
  nodeId: string
): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>([nodeId]);
  const queue = [...(childrenByParent[nodeId] ?? [])];

  while (queue.length) {
    const node = queue.shift() as AORHierarchyNode;
    if (seen.has(node.id)) continue;
    seen.add(node.id);
    ids.push(node.id);
    queue.push(...(childrenByParent[node.id] ?? []));
  }

  return ids;
};
